import React, { FC, RefObject, useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, PaperProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AuthService } from 'services/AuthService'
import { useSnackbar } from 'notistack'
import Draggable from 'react-draggable'
import ListTransfer from 'components/transfer/ListTransfer'
import CloseIcon from '@mui/icons-material/Close'

interface AssignTagsProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    select?: any;
    reloading: () => void;
    tags?: any;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const AssignTags: FC<AssignTagsProps> = ({ open, setOpen, select, reloading, tags }) => {
    const { t } = useTranslation()
    const [elementos, setElementos] = useState<any[]>([])
    const [selectedElements, setSelectedElements] = useState<any[]>([])
    const { enqueueSnackbar } = useSnackbar()
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return

        try {
            if (select?.length > 0) {
                const normalizedTags = (tags || []).map((tag: any) => ({
                    ...tag,
                    id: tag.id || tag.tag_id,
                }))
                setElementos(normalizedTags)

                const normalizedSelectedTags = (select[0]?.tags || []).map((tag: any) => {
                    const id = tag.id || tag.tag_id
                    const found = normalizedTags.find((item: any) => item.id === id)
                    return {
                        ...found,
                        ...tag,
                        id,
                        name: tag.name || found?.name || `${id}`,
                    }
                })
                setSelectedElements(normalizedSelectedTags)
            }
        } catch (error) {
            console.error('Error fetching tags:', error)
        }
    }, [open, select, tags])

    const addAssignTags = async () => {
        const authService = new AuthService()
        const accessToken = localStorage.getItem('access_token')
        if (!accessToken) {
            console.error('No access token found in localStorage')
            return
        }

        try {
            setLoading(true)
            await authService.profile.assignTag({
                profile_id: select[0].profile_id,
                tag_ids: selectedElements.map((item: any) => item.id),
            })
            enqueueSnackbar(t('profiles.table.dialog.assign_tags.messages.success'), { variant: 'success' })
            reloading()
            setOpen(false)
        } catch (error) {
            console.error('Error:', error)
            enqueueSnackbar(t('profiles.table.dialog.assign_tags.messages.error'), { variant: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('profiles.table.dialog.assign_tags.title')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <ListTransfer
                    loading={loading}
                    allItems={elementos}
                    right={selectedElements}
                    setRight={setSelectedElements}
                    textLeft={t('profiles.table.dialog.assign_tags.available_list')}
                    textRight={t('profiles.table.dialog.assign_tags.assigned_list')}
                    textSelected={t('general.transfer_list.selected')}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={() => setOpen(false)} color="error">
                    {t('profiles.table.dialog.assign_tags.buttons.cancel')}
                </Button>
                <Button variant='contained' onClick={() => addAssignTags()} color="primary" loading={loading}>
                    {t('profiles.table.dialog.assign_tags.buttons.assign')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { AssignTags }
